/**
 * Admin — Promotion Auto-Assign Rules (batch #5).
 * Manage `promotion_state_rules`: the state -> offer source of truth the
 * auto-assign engine reads. Ships DORMANT (per-director toggle, default off),
 * so editing rules here has no effect until a director is toggled on.
 */

import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { TrashIcon, PlusIcon } from "@heroicons/react/24/outline";
import {
  getPromotionStateRules,
  createPromotionStateRule,
  updatePromotionStateRule,
  deletePromotionStateRule,
  type PromotionClassification,
  type PromotionStateRule,
} from "../../services/promotionRules.service";
import { getStateClassifications } from "../../services/partnerOffers.service";
import { getAvailableOffers } from "../../services/eventOffers.service";
import { useToast } from "../../components/ui/Toast";
import Button from "../../components/common/Button";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import Badge from "../../components/ui/Badge";

const CLASSIFICATION_LABELS: Record<PromotionClassification, string> = {
  insurance: "Insurance",
  non_insurance: "Non-Insurance",
};

const PromotionRules: React.FC = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const [newState, setNewState] = useState("");
  const [newOfferId, setNewOfferId] = useState("");
  const [newClassification, setNewClassification] = useState<
    "" | PromotionClassification
  >("");

  const { data: rules, isLoading: rulesLoading } = useQuery({
    queryKey: ["promotion-state-rules"],
    queryFn: getPromotionStateRules,
  });
  const { data: states } = useQuery({
    queryKey: ["state-classifications"],
    queryFn: getStateClassifications,
  });
  const { data: offers } = useQuery({
    queryKey: ["available-offers"],
    queryFn: getAvailableOffers,
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ["promotion-state-rules"] });

  const createMutation = useMutation({
    mutationFn: () =>
      createPromotionStateRule({
        state_code: newState,
        offer_id: newOfferId,
        applies_to_classification: newClassification || null,
      }),
    onSuccess: () => {
      invalidate();
      setNewState("");
      setNewOfferId("");
      setNewClassification("");
      showToast("success", "Rule added");
    },
    onError: (err: any) => {
      if (err?.code === "23505") {
        showToast("error", "A rule for that state + offer already exists");
      } else {
        showToast("error", err?.message || "Failed to add rule");
      }
    },
  });

  const toggleMutation = useMutation({
    mutationFn: (rule: PromotionStateRule) =>
      updatePromotionStateRule(rule.id, { is_active: !rule.is_active }),
    onSuccess: () => invalidate(),
    onError: (err: any) =>
      showToast("error", err?.message || "Failed to update rule"),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deletePromotionStateRule(id),
    onSuccess: () => {
      invalidate();
      showToast("success", "Rule removed");
    },
    onError: (err: any) =>
      showToast("error", err?.message || "Failed to remove rule"),
  });

  // Group rules by state for display
  const grouped = useMemo(() => {
    const map = new Map<string, PromotionStateRule[]>();
    for (const r of rules || []) {
      const arr = map.get(r.state_code) || [];
      arr.push(r);
      map.set(r.state_code, arr);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [rules]);

  const stateName = (code: string) =>
    states?.find((s) => s.state_code === code)?.state_name || code;

  const canAdd = newState && newOfferId && !createMutation.isPending;

  return (
    <div className="p-6 space-y-6 max-w-4xl">
      <div>
        <h1 className="text-xl font-bold text-gray-900">
          Promotion Auto-Assign Rules
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Map a state to the promotion(s) that should be auto-attached to events
          in that state. Rules only take effect for a director once their
          auto-assign toggle is turned on (Sales Team → director → Promotion
          Auto-Assign). Already issued vouchers are never affected.
        </p>
      </div>

      {/* Add rule */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Add Rule</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              State *
            </label>
            <select
              value={newState}
              onChange={(e) => setNewState(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            >
              <option value="">Select a state…</option>
              {(states || []).map((s) => (
                <option key={s.state_code} value={s.state_code}>
                  {s.state_code} — {s.state_name} (
                  {CLASSIFICATION_LABELS[s.classification]})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Promotion (Offer) *
            </label>
            <select
              value={newOfferId}
              onChange={(e) => setNewOfferId(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            >
              <option value="">Select an offer…</option>
              {(offers || []).map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name} ({o.offer_code})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Classification filter
            </label>
            <select
              value={newClassification}
              onChange={(e) =>
                setNewClassification(
                  e.target.value as "" | PromotionClassification,
                )
              }
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            >
              <option value="">Any (all events in state)</option>
              <option value="insurance">Insurance states only</option>
              <option value="non_insurance">Non-Insurance states only</option>
            </select>
          </div>
          <div>
            <Button
              onClick={() => createMutation.mutate()}
              disabled={!canAdd}
              className="w-full"
            >
              <PlusIcon className="h-4 w-4 mr-1" />
              {createMutation.isPending ? "Adding…" : "Add"}
            </Button>
          </div>
        </div>
      </div>

      {/* Rules list */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Rules ({rules?.length || 0})
        </h2>
        {rulesLoading ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner />
          </div>
        ) : grouped.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-8">
            No rules yet. Add a state → promotion mapping above.
          </p>
        ) : (
          <div className="space-y-6">
            {grouped.map(([code, stateRules]) => (
              <div key={code}>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">
                  {code} — {stateName(code)}
                </h3>
                <div className="space-y-2">
                  {stateRules.map((rule) => (
                    <div
                      key={rule.id}
                      className="flex items-center justify-between py-2 px-3 border border-gray-100 rounded-lg"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <Badge
                          color={rule.is_active ? "green" : "gray"}
                          size="sm"
                        >
                          {rule.is_active ? "Active" : "Inactive"}
                        </Badge>
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate">
                            {rule.offer?.name || "(offer removed)"}
                            {rule.offer?.offer_code
                              ? ` (${rule.offer.offer_code})`
                              : ""}
                          </div>
                          <div className="text-xs text-gray-500">
                            {rule.applies_to_classification
                              ? `${CLASSIFICATION_LABELS[rule.applies_to_classification]} states only`
                              : "Applies to any event in this state"}
                            {rule.offer && rule.offer.is_active === false
                              ? " · ⚠ offer inactive"
                              : ""}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => toggleMutation.mutate(rule)}
                          disabled={toggleMutation.isPending}
                        >
                          {rule.is_active ? "Deactivate" : "Activate"}
                        </Button>
                        <button
                          onClick={() => deleteMutation.mutate(rule.id)}
                          className="text-gray-400 hover:text-red-600"
                          title="Remove rule"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PromotionRules;
